import { Button, ContentfulImage, ContentLayout, ItemsContainer } from "@2u-landing-pages/components";
import {
  ContentfulImageConfig,
  TypeButtonWithoutUnresolvableLinksResponse,
  TypeContent_liteWithoutUnresolvableLinksResponse,
  TypeItems_containerWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { Body, ButtonGroup, Container } from "@red-digital/bricks";
import cn from "classnames";
import React, { useMemo } from "react";
import { CustomCSSProperties, DetailCalloutProps } from "../../DetailCallout.types";

// @ts-ignore
import { renderText } from "@2u-landing-pages/utils/contentful-richtext";
import styles from "./Default.module.scss";

const spacingMap: Record<string, string> = {
  "Large (24/16)": "large",
  "Medium (16/12)": "medium",
  "Small (8/4)": "small",
  "None (0/0)": "none",
};

export const Default_v2: React.FC<DetailCalloutProps> = ({
  background_color,
  className,
  content,
  header_image,
  image_config,
  desktop_orientation = "stacked",
  mobile_orientation = "stacked",
  stacked_layout_padding = "Large (24/16)",
  mobile_reverse_order = false,
  footnote,
  footnote_background_color,
  footnote_text_color,
  phoneNumber,
  parseMetadata,
}) => {
  const style: CustomCSSProperties = {};

  if (background_color) style["--bg-color"] = `var(${background_color})`;
  if (footnote_background_color) style["--footnote-bg-color"] = `var(${footnote_background_color})`;
  if (footnote_text_color) style["--footnote-color"] = `var(${footnote_text_color})`;

  const renderFootnote = useMemo(() => {
    return footnote ? renderText(parseMetadata ? parseMetadata(footnote) : footnote, phoneNumber) : "";
  }, [footnote, parseMetadata, phoneNumber]);

  return (
    <div
      className={cn(className, styles.detailCallout, {
        [styles[`spacing-mobile-${spacingMap[stacked_layout_padding]}`]]: mobile_orientation === "stacked",
        [styles[`spacing-desktop-${spacingMap[stacked_layout_padding]}`]]: desktop_orientation === "stacked",
      })}
      style={style}
    >
      {header_image?.fields?.file?.url && (
        <div className={styles.featuredImage}>
          <ContentfulImage image={header_image} config={image_config as ContentfulImageConfig | undefined} />
        </div>
      )}
      <Container
        className={cn(styles.content, {
          [styles.desktopStacked]: desktop_orientation === "stacked",
          [styles.desktopInline]: desktop_orientation === "inline",
          [styles.mobileStacked]: mobile_orientation === "stacked",
          [styles.mobileInline]: mobile_orientation === "inline",
          [styles.mobileReverseOrder]: mobile_reverse_order && mobile_orientation === "stacked",
        })}
      >
        {content?.map((section, index) => {
          if (!section) return null;

          if (!section?.sys?.contentType?.sys?.id) return null;

          if (section?.sys?.contentType?.sys?.id === "content_lite") {
            const content = section as TypeContent_liteWithoutUnresolvableLinksResponse;
            return (
              <ContentLayout
                key={index}
                className={styles.heading}
                content={content}
                heading={{
                  variant: "heading-xl",
                }}
              />
            );
          }

          if (section?.sys?.contentType?.sys?.id === "button") {
            const button = section as TypeButtonWithoutUnresolvableLinksResponse;
            return (
              <ButtonGroup
                placement="center"
                key={section?.sys.id}
                className={cn(styles.button, {
                  [styles.desktopInline]: desktop_orientation === "inline",
                  [styles.mobileInline]: mobile_orientation === "inline",
                })}
              >
                <Button location="Detail Callout" {...button?.fields}>
                  {button?.fields.label}
                </Button>
              </ButtonGroup>
            );
          }

          if (section?.sys?.contentType?.sys?.id === "items_container") {
            const container = section as TypeItems_containerWithoutUnresolvableLinksResponse;
            return (
              <ItemsContainer
                key={index}
                entry={container}
                orientation="horizontal"
                heading={{ variant: "heading-lg" }}
                body={{ variant: "body-sm" }}
              />
            );
          }
        })}
      </Container>
      {footnote && (
        <Body
          variant="body-sm"
          className={cn(styles.footnote, {
            [styles.noBackground]: !footnote_background_color,
          })}
        >
          {renderFootnote}
        </Body>
      )}
    </div>
  );
};
